import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { base44 } from "api/apiClient";
import Layout from "../components/Layout";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Card, CardContent } from "components/ui/card";
import { Bot, Send, Loader2, TrendingUp, Sparkles, Calendar, X, FileText, Mic, Pause, Square, Play, CheckCircle2, AlertTriangle, Package, ChefHat, Radio, Store, Network, MessageSquare, RefreshCw, Plus, Pencil, Camera, Image as ImageIcon, ChevronLeft } from "lucide-react";
import { Badge } from "components/ui/badge";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import { format } from "date-fns";
import { buildMemoryGraphLayout, pruneMemoryNodePositions } from "../utils/memoryGraph";

const MEMORY_INDEXING_PROMPT = [
  "Run a memory indexing pass for the current authenticated user and active restaurant only.",
  "Start by calling index_memory_pages only to sync the catalog and baseline obvious links.",
  "Then call list_memory_pages with limit=100.",
  "Read every active non-index memory page returned by the list; do not decide relationships from titles or previews alone.",
  "Compare the full page contents and make the graph wiki-like: add clear node-to-node [[...]] links when a relationship is factually justified by the pages.",
  "Relationships can be semantic, not only literal slug mentions: shared people, responsibilities, projects, workflows, decisions, entities, events, locations, or one page explaining/refining another page.",
  "When a relationship should be linked, call link_memory_pages with the source page, target page, reason, and bidirectional=true for important mutual relationships.",
  "Do not claim that a link was added unless link_memory_pages confirms it.",
  "Maintain concise ## Related sections with bullet links and a short reason for each link.",
  "Do not link pages only because they share generic words; the reason must be specific and grounded in the page text.",
  "Do not invent facts and do not use or mention any other user's or restaurant's memory.",
  "Answer with a short summary of what you indexed and any links you changed.",
].join(" ");

const THREE_DOTS = "...";

const ONBOARDING_INSTRUCTIONS = [
  "You are onboarding a restaurant into Carlo.",
  "Guide the operator through the setup conversation and collect the details Carlo needs to run forecasts, costs, margins, ordering, and staff planning.",
  "Prioritize: restaurant locations and opening hours, POS or sales import status, menu products and prices, recipe cards, supplier or order sheets, and shift schedules.",
  "Keep a running onboarding checklist covering every item Carlo needs. For each item track its status — not started, in progress, or done — and note what you have already stored versus what is still missing.",
  "At the end of each reply, show the updated checklist so the operator can see what is done, what is still undone, and what you need from them next.",
  "When files or photos are attached, inspect them through the normal Carlo tools and create or update the relevant records.",
  "Ask for the next missing piece when information is incomplete. Keep the conversation practical and action-oriented.",
].join("\n");

const activityElapsedLabel = (activity, firstAt) => {
  if (!activity?.at || !firstAt) return "";
  const at = Date.parse(activity.at);
  if (!Number.isFinite(at)) return "";
  const seconds = Math.max(0, Math.floor((at - firstAt) / 1000));
  return seconds < 60 ? `+${seconds}s` : `+${Math.floor(seconds / 60)}m ${seconds % 60}s`;
};

export default function CarloAgent({ onboardingMode = false, embedded = false, onboardingChatsOnly = false }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { completeOnboarding } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  // Which conversation the in-flight run belongs to. A chat turn is a blocking
  // POST, so switching conversations (or starting a new one) mid-run must NOT
  // show that run's "analyzing" state in the other conversation, and the
  // resolving reply must land only in its own conversation.
  const [loadingConversationId, setLoadingConversationId] = useState(null);
  const [recordingStatus, setRecordingStatus] = useState("idle");
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [fileError, setFileError] = useState("");
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const attachedFilesRef = useRef([]);
  const attachmentMenuRef = useRef(null);
  const inputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const memoryGraphSvgRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const audioChunksRef = useRef([]);
  const messagesRef = useRef([]);
  const currentConversationIdRef = useRef(null);
  const streamControllerRef = useRef(null);
  const streamKeyRef = useRef(null);
  const dragDepthRef = useRef(0);
  const activitiesRef = useRef([]);
  const activityCursorRef = useRef(null);

  const MAX_ATTACHMENT_MB = 30;
  const MAX_ATTACHMENTS = 8;
  const [streamStatus, setStreamStatus] = useState("idle");
  const [agentActivities, setAgentActivities] = useState([]);
  const [onboardingError, setOnboardingError] = useState("");
  const [onboardingCompleting, setOnboardingCompleting] = useState(false);
  const [activeView, setActiveView] = useState("chat");
  // On mobile the conversation list and the chat are shown one at a time
  // (master-detail). false = show the list, true = show the chat/memory pane.
  const [mobileShowChat, setMobileShowChat] = useState(false);
  const [memoryGraph, setMemoryGraph] = useState({ nodes: [], links: [], missing_links: [] });
  const [memoryGraphLoading, setMemoryGraphLoading] = useState(false);
  const [memoryNodePositions, setMemoryNodePositions] = useState({});
  const [draggingMemorySlug, setDraggingMemorySlug] = useState(null);
  const [selectedMemorySlug, setSelectedMemorySlug] = useState(null);
  const [selectedMemoryPage, setSelectedMemoryPage] = useState(null);
  const [selectedMemoryLoading, setSelectedMemoryLoading] = useState(false);
  const [selectedMemoryError, setSelectedMemoryError] = useState("");
  const [showMemoryForm, setShowMemoryForm] = useState(false);
  const [memoryForm, setMemoryForm] = useState({
    title: "",
    body_markdown: "",
  });
  const [memoryFormError, setMemoryFormError] = useState("");
  const [memorySaving, setMemorySaving] = useState(false);
  const [memoryIndexing, setMemoryIndexing] = useState(false);
  const [memoryIndexError, setMemoryIndexError] = useState("");
  const [editingMemorySlug, setEditingMemorySlug] = useState(null);
  const [memoryEditForm, setMemoryEditForm] = useState({
    title: "",
    body_markdown: "",
  });
  const [memoryEditError, setMemoryEditError] = useState("");
  const [memoryEditSaving, setMemoryEditSaving] = useState(false);
  const memoryDragRef = useRef({ slug: null, pointerId: null });

  const appendMessages = useCallback((currentMessages, incomingMessages = []) => {
    const nextMessages = [...currentMessages];

    incomingMessages.forEach((message) => {
      if (!message) return;

      if (message.id) {
        const existingIndex = nextMessages.findIndex((candidate) => candidate?.id === message.id);
        if (existingIndex >= 0) {
          nextMessages[existingIndex] = message;
          return;
        }
      }

      if (message.role === "user") {
        const optimisticIndex = nextMessages.findIndex((candidate) => (
          typeof candidate?.id === "string"
          && candidate.id.startsWith("local-")
          && candidate.role === "user"
          && candidate.content === message.content
        ));
        if (optimisticIndex >= 0) {
          nextMessages[optimisticIndex] = message;
          return;
        }
      }

      nextMessages.push(message);
    });

    return nextMessages;
  }, []);

  const appendActivities = useCallback((currentActivities, incomingActivities = []) => {
    const nextActivities = [...currentActivities];
    incomingActivities.forEach((activity) => {
      if (!activity) return;
      if (activity.id && nextActivities.some((candidate) => candidate?.id === activity.id)) return;
      nextActivities.push(activity);
    });
    return nextActivities;
  }, []);

  const getLatestActiveJobMessage = (items = messages) => {
    for (let index = items.length - 1; index >= 0; index -= 1) {
      const message = items[index];
      if (message?.metadata?.job_id && ["queued", "running"].includes(message?.metadata?.job_status)) {
        return message;
      }
    }
    return null;
  };

  const getOnboardingDocumentLabel = (documentKind) => {
    if (documentKind === "menu_pdf") {
      return t("carloAgent.menuOnboarding", "Menu onboarding");
    }
    if (documentKind === "supplier_order_sheet") {
      return t("carloAgent.supplierSheetOnboarding", "Supplier sheet onboarding");
    }
    return t("carloAgent.recipeCardOnboarding", "Recipe card onboarding");
  };

  const getOnboardingEventConfig = (message) => {
    const jobStatus = message?.metadata?.job_status;
    const kind = message?.metadata?.kind;
    const documentKind = message?.metadata?.document_kind;

    if (jobStatus === "failed" || kind === "error") {
      return {
        title: t("carloAgent.onboardingFailed", "Onboarding failed"),
        badge: t("carloAgent.failed", "Failed"),
        icon: AlertTriangle,
        wrapperClass: "border-red-200 bg-red-50/80",
        iconClass: "bg-red-100 text-red-600",
        badgeClass: "border-red-200 bg-red-100 text-red-700",
      };
    }

    if (jobStatus === "done" || kind === "summary") {
      return {
        title: t("carloAgent.onboardingDone", "Onboarding complete"),
        badge: t("carloAgent.done", "Done"),
        icon: CheckCircle2,
        wrapperClass: "border-emerald-200 bg-emerald-50/80",
        iconClass: "bg-emerald-100 text-emerald-700",
        badgeClass: "border-emerald-200 bg-emerald-100 text-emerald-700",
      };
    }

    if (kind === "job_started") {
      return {
        title: getOnboardingDocumentLabel(documentKind),
        badge: t("carloAgent.live", "Live"),
        icon: Radio,
        wrapperClass: "border-emerald-200 bg-emerald-50/80",
        iconClass: "bg-emerald-100 text-emerald-700",
        badgeClass: "border-emerald-200 bg-emerald-100 text-emerald-700",
      };
    }

    if (kind === "recipe_created") {
      return {
        title: documentKind === "menu_pdf"
          ? t("carloAgent.menuProductSaved", "Menu product saved")
          : t("carloAgent.recipeSaved", "Recipe saved"),
        badge: t("carloAgent.saved", "Saved"),
        icon: documentKind === "menu_pdf" ? Store : ChefHat,
        wrapperClass: "border-slate-200 bg-white",
        iconClass: documentKind === "menu_pdf" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700",
        badgeClass: "border-emerald-200 bg-emerald-100 text-emerald-700",
      };
    }

    if (kind === "recipe_updated") {
      return {
        title: documentKind === "menu_pdf"
          ? t("carloAgent.menuProductUpdated", "Menu product updated")
          : t("carloAgent.recipeUpdated", "Recipe updated"),
        badge: t("carloAgent.updated", "Updated"),
        icon: documentKind === "menu_pdf" ? Store : ChefHat,
        wrapperClass: "border-slate-200 bg-white",
        iconClass: documentKind === "menu_pdf" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700",
        badgeClass: "border-sky-200 bg-sky-100 text-sky-700",
      };
    }

    if (kind === "inventory_created") {
      return {
        title: t("carloAgent.inventoryAdded", "Inventory item added"),
        badge: t("carloAgent.added", "Added"),
        icon: Package,
        wrapperClass: "border-slate-200 bg-white",
        iconClass: "bg-sky-100 text-sky-700",
        badgeClass: "border-sky-200 bg-sky-100 text-sky-700",
      };
    }

    if (kind === "inventory_updated") {
      return {
        title: t("carloAgent.inventoryUpdated", "Inventory updated"),
        badge: t("carloAgent.updated", "Updated"),
        icon: Package,
        wrapperClass: "border-slate-200 bg-white",
        iconClass: "bg-sky-100 text-sky-700",
        badgeClass: "border-sky-200 bg-sky-100 text-sky-700",
      };
    }

    if (kind === "supplier_created") {
      return {
        title: t("carloAgent.supplierAdded", "Supplier added"),
        badge: t("carloAgent.added", "Added"),
        icon: FileText,
        wrapperClass: "border-slate-200 bg-white",
        iconClass: "bg-violet-100 text-violet-700",
        badgeClass: "border-violet-200 bg-violet-100 text-violet-700",
      };
    }

    if (kind === "warning") {
      return {
        title: t("carloAgent.warning", "Needs review"),
        badge: t("carloAgent.warningBadge", "Warning"),
        icon: AlertTriangle,
        wrapperClass: "border-amber-200 bg-amber-50/80",
        iconClass: "bg-amber-100 text-amber-700",
        badgeClass: "border-amber-200 bg-amber-100 text-amber-700",
      };
    }

    return {
      title: t("carloAgent.workerProgress", "Worker progress"),
      badge: t("carloAgent.live", "Live"),
      icon: Loader2,
      wrapperClass: "border-slate-200 bg-white",
      iconClass: "bg-slate-100 text-slate-700",
      badgeClass: "border-slate-200 bg-slate-100 text-slate-700",
      spinIcon: true,
    };
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, agentActivities]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    currentConversationIdRef.current = currentConversationId;
  }, [currentConversationId]);

  // The run indicator + composer-disabled state belong to the conversation that
  // started the run, not to the app globally. A different (or brand-new)
  // conversation stays idle even while another one is still working.
  const isCurrentConversationLoading = isLoading && loadingConversationId === currentConversationId;

  useEffect(() => {
    activitiesRef.current = agentActivities;
  }, [agentActivities]);

  useEffect(() => {
    loadConversations();
  }, []);

  useEffect(() => {
    if (activeView === "memory") {
      loadMemoryGraph();
    }
  }, [activeView]);

  useEffect(() => {
    setMemoryNodePositions((current) => {
      const next = pruneMemoryNodePositions(current, memoryGraph.nodes || []);
      return Object.keys(next).length === Object.keys(current).length ? current : next;
    });
  }, [memoryGraph.nodes]);

  // Load messages when switching to a conversation
  useEffect(() => {
    if (currentConversationId) {
      base44.agents.getConversation(currentConversationId)
        .then((data) => {
          setMessages(data.messages || []);
          setAgentActivities([]);
          activityCursorRef.current = data.latest_activity_id || null;
        })
        .catch((err) => console.error("Error loading messages:", err));
      return;
    }
    setMessages([]);
    setAgentActivities([]);
    activityCursorRef.current = null;
    setStreamStatus("idle");
  }, [currentConversationId]);

  useEffect(() => {
    if (recordingStatus === "recording") {
      recordingTimerRef.current = window.setInterval(() => {
        setRecordingSeconds((prev) => prev + 1);
      }, 1000);
      return () => {
        window.clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      };
    }

    if (recordingTimerRef.current) {
      window.clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
  }, [recordingStatus]);

  useEffect(() => () => {
    if (recordingTimerRef.current) {
      window.clearInterval(recordingTimerRef.current);
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
    }
    if (streamControllerRef.current) {
      streamControllerRef.current.abort();
    }
  }, []);

  useEffect(() => {
    if (!showAttachMenu) return undefined;
    const handleMouseDown = (event) => {
      if (attachmentMenuRef.current?.contains(event.target)) return;
      setShowAttachMenu(false);
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [showAttachMenu]);

  const buildConversationMetadata = (overrides = {}) => {
    if (onboardingMode || onboardingChatsOnly) {
      return {
        name: t("carloAgent.onboardingConversationName", "Carlo onboarding"),
        description: t("carloAgent.onboardingConversationDescription", "Restaurant setup conversation"),
        onboarding_mode: true,
        onboarding_instructions: ONBOARDING_INSTRUCTIONS,
        ...overrides,
      };
    }

    return {
      name: `Carlo Session - ${format(new Date(), 'dd.MM.yyyy HH:mm')}`,
      description: t("carloAgent.dayForecastPrompt"),
      ...overrides,
    };
  };

  const createCarloConversation = async (metadataOverrides = {}) => {
    const conversation = await base44.agents.createConversation({
      agent_name: "carlo_forecast",
      metadata: buildConversationMetadata(metadataOverrides),
    });
    if (!conversation?.id) {
      throw new Error("Could not create conversation.");
    }
    return conversation;
  };

  const loadConversations = async () => {
    try {
      const convos = await base44.agents.listConversations({
        agent_name: "carlo_forecast",
      });
      const visibleConversations = (onboardingMode || onboardingChatsOnly)
        ? (convos || []).filter((conversation) => conversation?.metadata?.onboarding_mode)
        : (convos || []);
      setConversations(visibleConversations);

      const hasCurrentConversation = currentConversationId
        && visibleConversations.some((conversation) => conversation.id === currentConversationId);

      if (visibleConversations.length === 0 && onboardingMode && !currentConversationId) {
        const conversation = await createCarloConversation();
        setConversations([conversation]);
        setCurrentConversationId(conversation.id);
        setMessages([]);
        setAgentActivities([]);
        activityCursorRef.current = null;
        return;
      }

      if (visibleConversations.length > 0 && !hasCurrentConversation) {
        const latestConvo = visibleConversations[0];
        setCurrentConversationId(latestConvo.id);
        const detail = await base44.agents.getConversation(latestConvo.id);
        setMessages(detail.messages || []);
        activityCursorRef.current = detail.latest_activity_id || null;
      }
    } catch (error) {
      console.error("Error loading conversations:", error);
    }
  };

  const loadMemoryPage = async (slug) => {
    if (!slug) {
      setSelectedMemoryPage(null);
      setSelectedMemoryError("");
      return;
    }

    setSelectedMemoryLoading(true);
    setSelectedMemoryError("");
    try {
      const page = await base44.agents.getMemoryPage(slug);
      setSelectedMemoryPage(page);
    } catch (error) {
      setSelectedMemoryPage(null);
      setSelectedMemoryError(t("carloAgent.memoryPageLoadError", "Could not load this memory."));
    } finally {
      setSelectedMemoryLoading(false);
    }
  };

  const selectMemoryNode = async (slug) => {
    setSelectedMemorySlug(slug);
    setEditingMemorySlug(null);
    setMemoryEditError("");
    await loadMemoryPage(slug);
  };

  const loadMemoryGraph = async (preferredSlug = null) => {
    setMemoryGraphLoading(true);
    try {
      const graph = await base44.agents.getMemoryGraph();
      const nextGraph = {
        nodes: graph?.nodes || [],
        links: graph?.links || [],
        missing_links: graph?.missing_links || [],
      };
      setMemoryGraph(nextGraph);
      const nextSelectedSlug = (
        preferredSlug && nextGraph.nodes.some((node) => node.slug === preferredSlug)
          ? preferredSlug
          : selectedMemorySlug && nextGraph.nodes.some((node) => node.slug === selectedMemorySlug)
            ? selectedMemorySlug
            : nextGraph.nodes[0]?.slug || null
      );
      setSelectedMemorySlug(nextSelectedSlug);
      await loadMemoryPage(nextSelectedSlug);
    } finally {
      setMemoryGraphLoading(false);
    }
  };

  const updateMemoryForm = (field, value) => {
    setMemoryForm((current) => ({ ...current, [field]: value }));
    setMemoryFormError("");
  };

  const resetMemoryForm = () => {
    setMemoryForm({
      title: "",
      body_markdown: "",
    });
    setMemoryFormError("");
  };

  const handleSaveMemory = async (event) => {
    event.preventDefault();
    if (memorySaving) return;

    setMemorySaving(true);
    setMemoryFormError("");
    try {
      const saved = await base44.agents.createMemoryPage(memoryForm);
      resetMemoryForm();
      setShowMemoryForm(false);
      await loadMemoryGraph(saved?.slug || null);
    } catch (error) {
      const data = error?.response?.data || {};
      const firstError = Object.values(data).flat?.()[0] || data.detail;
      setMemoryFormError(firstError || t("carloAgent.memorySaveError", "Could not save memory."));
    } finally {
      setMemorySaving(false);
    }
  };

  const startEditingMemory = () => {
    if (!selectedMemoryNode) return;
    setEditingMemorySlug(selectedMemoryNode.slug);
    setMemoryEditForm({
      title: selectedMemoryPage?.title || selectedMemoryNode.title || selectedMemoryNode.slug,
      body_markdown: selectedMemoryPage?.body_markdown || "",
    });
    setMemoryEditError("");
  };

  const updateMemoryEditForm = (field, value) => {
    setMemoryEditForm((current) => ({ ...current, [field]: value }));
    setMemoryEditError("");
  };

  const cancelEditingMemory = () => {
    setEditingMemorySlug(null);
    setMemoryEditError("");
  };

  const handleUpdateMemory = async (event) => {
    event.preventDefault();
    if (memoryEditSaving || !editingMemorySlug) return;

    setMemoryEditSaving(true);
    setMemoryEditError("");
    try {
      const saved = await base44.agents.updateMemoryPage(editingMemorySlug, memoryEditForm);
      setSelectedMemoryPage(saved);
      setEditingMemorySlug(null);
      await loadMemoryGraph(saved?.slug || editingMemorySlug);
    } catch (error) {
      const data = error?.response?.data || {};
      const firstError = Object.values(data).flat?.()[0] || data.detail;
      setMemoryEditError(firstError || t("carloAgent.memoryUpdateError", "Could not update memory."));
    } finally {
      setMemoryEditSaving(false);
    }
  };

  const handleRunMemoryIndex = async () => {
    if (memoryIndexing) return;

    setMemoryIndexing(true);
    setMemoryIndexError("");
    try {
      const conversation = await base44.agents.createConversation({
        agent_name: "carlo_forecast",
        metadata: {
          name: `Memory Indexing - ${format(new Date(), 'dd.MM.yyyy HH:mm')}`,
          description: "Memory indexing pass",
        },
      });
      if (!conversation?.id) {
        throw new Error("Could not create indexing conversation.");
      }

      setCurrentConversationId(conversation.id);
      setMessages([]);
      await loadConversations();
      const result = await base44.agents.addMessage(
        { id: conversation.id },
        { role: "user", content: MEMORY_INDEXING_PROMPT },
      );
      const serverMessages = [result.user_message, result.assistant_message].filter(Boolean);
      if (serverMessages.length) {
        setMessages(serverMessages);
      }
      await loadMemoryGraph(selectedMemorySlug);
    } catch (error) {
      console.error("Error indexing memory:", error);
      setMemoryIndexError(t("carloAgent.memoryIndexError", "Could not index memory."));
    } finally {
      setMemoryIndexing(false);
    }
  };

  const createNewConversation = async () => {
    try {
      setMobileShowChat(true);
      const conversation = await createCarloConversation();
      setCurrentConversationId(conversation.id);
      setMessages([]);
      await loadConversations();
    } catch (error) {
      console.error("Error creating conversation:", error);
    }
  };

  const handleCompleteOnboarding = async () => {
    if (onboardingCompleting) return;

    setOnboardingCompleting(true);
    setOnboardingError("");
    try {
      await completeOnboarding();
      navigate("/dashboard", { replace: true });
    } catch (error) {
      console.error("Error completing onboarding:", error);
      setOnboardingError(
        error?.response?.data?.detail
        || error.message
        || t("carloAgent.onboardingCompleteFailed", "Could not open the dashboard. Please try again."),
      );
    } finally {
      setOnboardingCompleting(false);
    }
  };

  const handleAttachClick = () => {
    setFileError("");
    setShowAttachMenu((current) => !current);
  };

  const openFilePicker = () => {
    setFileError("");
    setShowAttachMenu(false);
    fileInputRef.current?.click();
  };

  const openCameraPicker = () => {
    setFileError("");
    setShowAttachMenu(false);
    cameraInputRef.current?.click();
  };

  const setAttachmentsFromFiles = (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return false;
    if (attachedFiles.length + files.length > MAX_ATTACHMENTS) {
      setFileError(t("carloAgent.attachmentTooMany", "Attach up to 8 files at once."));
      return false;
    }
    const tooLarge = files.find((file) => file.size > MAX_ATTACHMENT_MB * 1024 * 1024);
    if (tooLarge) {
      setFileError(t("carloAgent.attachmentTooLarge", "File is larger than 15 MB."));
      return false;
    }

    setFileError("");
    setAttachedFiles((current) => {
      const next = [...current, ...files];
      attachedFilesRef.current = next;
      return next;
    });
    return true;
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    setAttachmentsFromFiles(files);
  };

  const clearAttachment = (indexToRemove = null) => {
    setAttachedFiles((current) => {
      const next = indexToRemove === null
        ? []
        : current.filter((_, index) => index !== indexToRemove);
      attachedFilesRef.current = next;
      return next;
    });
    setFileError("");
  };

  const hasDraggedFiles = (event) =>
    Array.from(event.dataTransfer?.types || []).includes("Files");

  const handleDragEnter = (event) => {
    if (!hasDraggedFiles(event)) return;
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current += 1;
    setIsDraggingFile(true);
  };

  const handleDragOver = (event) => {
    if (!hasDraggedFiles(event)) return;
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = "copy";
    }
    if (!isDraggingFile) {
      setIsDraggingFile(true);
    }
  };

  const handleDragLeave = (event) => {
    if (!hasDraggedFiles(event)) return;
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) {
      setIsDraggingFile(false);
    }
  };

  const handleDrop = (event) => {
    if (!hasDraggedFiles(event)) return;
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current = 0;
    setIsDraggingFile(false);
    setAttachmentsFromFiles(event.dataTransfer?.files);
    inputRef.current?.focus();
  };

  // Paste an image/file from the clipboard. Handles every shape: blob items,
  // image/* items, clipboardData.files, and <img src> in pasted HTML (image
  // copied from a web page). On a paste with no usable image we show a message
  // (with detected formats) so it is never silently ignored.
  const pasteHandlerRef = useRef(null);
  pasteHandlerRef.current = (event) => {
    const clipboard = event.clipboardData || window.clipboardData;
    if (!clipboard) return;

    const renamed = (file) => {
      if (!file) return null;
      if (file.name && file.name !== "image.png") return file;
      const ext = (file.type && file.type.split("/")[1]) || "png";
      const stamp = new Date().toISOString().replace(/[:.]/g, "-");
      return new File([file], `pasted-${stamp}.${ext}`, { type: file.type || "image/png" });
    };

    const items = Array.from(clipboard.items || []);
    const files = [];
    items.forEach((item) => {
      const looksLikeFile =
        item.kind === "file" || (item.type && item.type.startsWith("image/"));
      if (looksLikeFile && typeof item.getAsFile === "function") {
        const f = renamed(item.getAsFile());
        if (f) files.push(f);
      }
    });
    if (!files.length) {
      Array.from(clipboard.files || []).forEach((file) => {
        const f = renamed(file);
        if (f) files.push(f);
      });
    }

    if (files.length) {
      event.preventDefault();
      setFileError("");
      setAttachmentsFromFiles(files);
      inputRef.current?.focus();
      return;
    }

    const html = (clipboard.getData && clipboard.getData("text/html")) || "";
    const match = html.match(/<img[^>]+src=["']([^"']+)["']/i);
    if (match && match[1]) {
      event.preventDefault();
      fetch(match[1])
        .then((r) => r.blob())
        .then((blob) => {
          const f = renamed(new File([blob], "image.png", { type: blob.type || "image/png" }));
          if (f) {
            setFileError("");
            setAttachmentsFromFiles([f]);
            inputRef.current?.focus();
          }
        })
        .catch(() =>
          setFileError(
            t("carloAgent.pasteImageFailed", "Couldn't read that image from the clipboard — try drag & drop instead."),
          ),
        );
      return;
    }

    const types = (clipboard.types && Array.from(clipboard.types).join(", ")) || "none";
    if (types !== "none" && types !== "text/plain") {
      setFileError(
        t("carloAgent.pasteNoImage", 'No image found on the clipboard. Use "Copy image" (not copy a file), or drag the file into the chat.') +
          ` [${types}]`,
      );
    }
  };

  useEffect(() => {
    const onPaste = (e) => pasteHandlerRef.current && pasteHandlerRef.current(e);
    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
  }, []);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    // Only block on a run that belongs to the conversation on screen — a
    // different conversation may still be working without gating this one.
    if (isCurrentConversationLoading) return;
    if (!input.trim() && attachedFilesRef.current.length === 0) return;

    setIsLoading(true);
    setLoadingConversationId(currentConversationId);
    const userMessage = input;
    const filesToSend = attachedFilesRef.current;
    const optimisticId = `local-${Date.now()}`;
    setInput("");
    attachedFilesRef.current = [];
    setAttachedFiles([]);
    setShowAttachMenu(false);
    setAgentActivities([]);

    const attachmentMetadata = filesToSend.map((file) => ({
      filename: file.name,
      size: file.size,
      media_type: file.type || "",
      kind: file.type?.startsWith("image/") ? "image" : undefined,
    }));
    const attachmentNames = filesToSend.map((file) => file.name).join(", ");

    // Show user message optimistically
    setMessages(prev => [...prev, {
      id: optimisticId,
      role: 'user',
      content: userMessage || (filesToSend.length ? `(attached ${attachmentNames})` : ""),
      metadata: filesToSend.length ? {
        attachment: attachmentMetadata[0],
        attachments: attachmentMetadata,
      } : undefined,
    }]);

    // Declared outside the try so the catch/finally can scope their writes to
    // the conversation this turn belongs to.
    let conversationId = currentConversationId;
    try {

      if (!conversationId) {
        const conversation = await createCarloConversation();
        conversationId = conversation.id;
        setCurrentConversationId(conversationId);
        setLoadingConversationId(conversationId);
        loadConversations();
      }

      const latestKnownMessageId = [...messagesRef.current].reverse().find((message) => message?.id)?.id || null;
      const latestKnownActivityId = activityCursorRef.current
        || [...activitiesRef.current].reverse().find((activity) => activity?.id)?.id
        || null;
      startConversationStream({
        conversationId,
        streamKey: `turn:${conversationId}:${optimisticId}`,
        afterMessageId: latestKnownMessageId,
        afterActivityId: latestKnownActivityId,
      });

      const result = await base44.agents.addMessage({ id: conversationId }, {
        role: "user",
        content: userMessage,
        files: filesToSend,
      });

      const serverMessages = [result.user_message, result.assistant_message].filter(Boolean);
      // The user may have switched away while this blocking turn ran — only
      // write the reply into the conversation it belongs to.
      if (serverMessages.length && currentConversationIdRef.current === conversationId) {
        setMessages((prev) => {
          const withoutOptimistic = prev.filter((message) => message?.id !== optimisticId);
          return appendMessages(withoutOptimistic, serverMessages);
        });
      }
    } catch (error) {
      console.error("Error sending message:", error);
      if (currentConversationIdRef.current === conversationId) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: 'Sorry, there was an error processing your message. Please try again.',
        }]);
        if (streamControllerRef.current) {
          streamControllerRef.current.abort();
          streamControllerRef.current = null;
          streamKeyRef.current = null;
        }
        setStreamStatus("error");
      }
    } finally {
      setIsLoading(false);
      setLoadingConversationId((prev) => (prev === conversationId ? null : prev));
    }
  };

  const handleComposerKeyDown = (event) => {
    if (event.isComposing || event.nativeEvent?.isComposing) {
      return;
    }
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      event.currentTarget.form?.requestSubmit();
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported("audio/webm")
          ? "audio/webm"
          : "audio/mp4",
      });

      audioChunksRef.current = [];
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
        const blob = new Blob(audioChunksRef.current, { type: mediaRecorder.mimeType });

        if (blob.size < 100) {
          setRecordingStatus("idle");
          return;
        }

        try {
          // Keep the full-page agent aligned with the backend transcription path.
          // Browser SpeechRecognition diverged too much from Yekar voice quality.
          const result = await base44.agents.transcribe(blob);
          if (result?.text) {
            setInput((prev) => (prev ? `${prev} ${result.text}` : result.text));
            inputRef.current?.focus();
          }
        } catch (error) {
          console.error("Transcription error:", error);
          alert(error?.response?.data?.detail || t("carlo.voiceError"));
        } finally {
          setRecordingStatus("idle");
        }
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setRecordingSeconds(0);
      setRecordingStatus("recording");
    } catch (error) {
      console.error("Microphone access error:", error);
      alert(t("carlo.voiceError"));
    }
  };

  const stopRecording = async () => {
    setRecordingStatus("transcribing");
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
      return;
    }
    setRecordingStatus("idle");
  };

  const togglePauseRecording = () => {
    if (!mediaRecorderRef.current) return;

    if (mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.pause();
      setRecordingStatus("paused");
      return;
    }

    if (mediaRecorderRef.current.state === "paused") {
      mediaRecorderRef.current.resume();
      setRecordingStatus("recording");
    }
  };

  const formatRecordingTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const formatAttachmentSize = (size) => {
    if (!size || Number.isNaN(size)) return null;
    return `${(size / 1024 / 1024).toFixed(1)} MB`;
  };

  const getMessageAttachments = (metadata = {}) => {
    if (Array.isArray(metadata.attachments) && metadata.attachments.length) {
      return metadata.attachments;
    }
    return metadata.attachment?.filename ? [metadata.attachment] : [];
  };

  const isAttachmentPlaceholderContent = (content, attachments = []) => {
    if (!attachments.length || !content) return false;
    const names = attachments.map((attachment) => attachment.filename).filter(Boolean).join(", ");
    if (!names) return false;
    return (
      content === `(${names})`
      || content === `(attached ${names})`
    );
  };

  const latestActiveJobMessage = getLatestActiveJobMessage(messages);
  const activeJobKey = currentConversationId && latestActiveJobMessage
    ? `${currentConversationId}:${latestActiveJobMessage.metadata.job_id}`
    : null;

  const startConversationStream = useCallback(({ conversationId, streamKey, afterMessageId, afterActivityId }) => {
    if (!conversationId || !streamKey) return;
    if (streamKeyRef.current === streamKey && streamControllerRef.current) return;
    if (streamControllerRef.current) {
      streamControllerRef.current.abort();
    }

    const controller = new AbortController();
    streamControllerRef.current = controller;
    streamKeyRef.current = streamKey;
    setStreamStatus("connecting");

    base44.agents.streamMessages(conversationId, {
      afterId: afterMessageId,
      afterActivityId,
      signal: controller.signal,
      onMessage: (message) => {
        setStreamStatus("live");
        setMessages((prev) => appendMessages(prev, [message]));
      },
      onActivity: (activity) => {
        setStreamStatus("live");
        if (activity?.id) activityCursorRef.current = activity.id;
        setAgentActivities((prev) => appendActivities(prev, [activity]));
      },
      onHeartbeat: () => {
        setStreamStatus((prev) => (prev === "connecting" ? "live" : prev));
      },
      onStatus: (packet) => {
        if (packet?.status === "idle") {
          if (packet?.after_activity_id) activityCursorRef.current = packet.after_activity_id;
          setStreamStatus("idle");
          controller.abort();
        }
      },
    }).catch((error) => {
      if (error?.name === "AbortError") return;
      console.error("Conversation stream error:", error);
      setStreamStatus("error");
    }).finally(() => {
      if (streamControllerRef.current === controller) {
        streamControllerRef.current = null;
        streamKeyRef.current = null;
      }
    });
  }, [appendActivities, appendMessages]);

  useEffect(() => {
    if (!activeJobKey || !currentConversationId) {
      if (streamKeyRef.current?.startsWith("job:") && streamControllerRef.current) {
        streamControllerRef.current.abort();
        streamControllerRef.current = null;
        streamKeyRef.current = null;
      }
      if (!isLoading && !streamKeyRef.current) setStreamStatus("idle");
      return undefined;
    }

    const latestKnownMessageId = [...messagesRef.current].reverse().find((message) => message?.id)?.id || null;
    const latestKnownActivityId = activityCursorRef.current
      || [...activitiesRef.current].reverse().find((activity) => activity?.id)?.id
      || null;
    const jobStreamKey = `job:${activeJobKey}`;
    startConversationStream({
      conversationId: currentConversationId,
      streamKey: jobStreamKey,
      afterMessageId: latestKnownMessageId,
      afterActivityId: latestKnownActivityId,
    });

    return () => {
      if (streamKeyRef.current === jobStreamKey && streamControllerRef.current) {
        streamControllerRef.current.abort();
      }
    };
  }, [activeJobKey, currentConversationId, isLoading, startConversationStream]);

  const quickActions = [
    { label: t("carloAgent.dayForecast"), prompt: t("carloAgent.dayForecastPrompt") },
    { label: t("carloAgent.weekForecast"), prompt: t("carloAgent.weekForecastPrompt") },
    { label: t("carloAgent.orderSuggestions"), prompt: t("carloAgent.orderSuggestionsPrompt") },
    { label: t("carloAgent.consumptionAnalysis"), prompt: t("carloAgent.consumptionAnalysisPrompt") },
  ];

  const AgentActivityTimeline = ({ activities, pending }) => {
    const timelineRef = useRef(null);
    const stickToBottomRef = useRef(true);
    const firstAt = useMemo(() => {
      const first = activities.find((activity) => activity?.at);
      return first?.at ? Date.parse(first.at) : undefined;
    }, [activities]);

    useEffect(() => {
      const node = timelineRef.current;
      if (!node || !stickToBottomRef.current) return;
      node.scrollTop = node.scrollHeight;
    }, [activities.length, pending]);

    const updateScrollIntent = () => {
      const node = timelineRef.current;
      if (!node) return;
      stickToBottomRef.current = node.scrollHeight - node.scrollTop - node.clientHeight < 48;
    };

    return (
      <div
        ref={timelineRef}
        onScroll={updateScrollIntent}
        aria-live={pending ? "polite" : undefined}
        className={`max-h-44 min-w-0 sm:min-w-[260px] overflow-y-auto rounded-xl border px-3 py-2 text-xs ${
          pending
            ? "border-emerald-200 bg-emerald-50/70 text-slate-700"
            : "border-slate-200 bg-slate-50 text-slate-600"
        }`}
      >
        {activities.map((activity) => {
          const elapsed = activityElapsedLabel(activity, firstAt);
          const isTool = String(activity.type || "").startsWith("tool");
          const isError = activity.status === "error";
          return (
            <div
              key={activity.id}
              className="grid grid-cols-[10px_minmax(0,1fr)_auto] items-start gap-2 py-1"
            >
              <span
                className={`mt-1.5 h-2 w-2 rounded-full ${
                  isError
                    ? "bg-red-500"
                    : pending && (activity.status === "active" || activity.id === activities[activities.length - 1]?.id)
                      ? "animate-pulse bg-emerald-600"
                      : isTool
                        ? "bg-slate-500"
                        : "bg-emerald-400"
                }`}
                aria-hidden="true"
              />
              <span className="min-w-0">
                <span className="block truncate font-medium text-slate-800">
                  {activity.label}
                </span>
                {activity.message ? (
                  <span className="mt-0.5 block truncate text-[11px] leading-snug text-slate-500">
                    {activity.message}
                  </span>
                ) : null}
              </span>
              {elapsed ? <span className="text-[11px] text-slate-400">{elapsed}</span> : null}
            </div>
          );
        })}
        {pending && (
          <div className="grid grid-cols-[10px_minmax(0,1fr)] items-center gap-2 py-1">
            <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-600" aria-hidden="true" />
            <span className="font-medium text-slate-800">
              {t("carloAgent.activityWorking", "Working")}{THREE_DOTS}
            </span>
          </div>
        )}
      </div>
    );
  };

  const MessageBubble = ({ message }) => {
    const isUser = message.role === 'user';
    const isOnboardingEvent = !isUser && message?.metadata?.job_id;
    const messageAttachments = getMessageAttachments(message?.metadata);
    const showMessageContent = message.content && !isAttachmentPlaceholderContent(message.content, messageAttachments);

    if (isOnboardingEvent) {
      const eventConfig = getOnboardingEventConfig(message);
      const EventIcon = eventConfig.icon;

      return (
        <div className="flex justify-start">
          <div className={`max-w-[85%] rounded-2xl border px-4 py-3 shadow-sm ${eventConfig.wrapperClass}`}>
            <div className="flex items-start gap-3">
              <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl ${eventConfig.iconClass}`}>
                <EventIcon className={`h-5 w-5 ${eventConfig.spinIcon ? "animate-spin" : ""}`} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm font-semibold text-slate-900">{eventConfig.title}</p>
                  <Badge className={`text-[11px] ${eventConfig.badgeClass}`}>
                    {eventConfig.badge}
                  </Badge>
                </div>
                {message.content && (
                  <p className="mt-1 text-sm leading-relaxed text-slate-700">{message.content}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <div className={`flex gap-3 ${isUser ? "justify-end" : "justify-start"}`}>
        {!isUser && (
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-emerald-600 to-emerald-700 flex items-center justify-center mt-0.5 shadow-md flex-shrink-0">
            <Bot className="w-5 h-5 text-white" />
          </div>
        )}
        <div className={`max-w-[85%] ${isUser && "flex flex-col items-end"}`}>
          {showMessageContent && (
            <div className={`rounded-2xl px-5 py-3 ${
              isUser 
                ? "bg-emerald-700 text-white shadow-md"
                : "bg-white border border-slate-200 shadow-sm"
            }`}>
              {isUser ? (
                <p className="text-sm leading-relaxed">{message.content}</p>
              ) : (
                <ReactMarkdown
                  className="text-sm prose prose-sm prose-slate max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0"
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeSanitize]}
                  components={{
                    p: ({ children }) => <p className="my-2 leading-relaxed">{children}</p>,
                    ul: ({ children }) => <ul className="my-2 ml-4 list-disc space-y-1">{children}</ul>,
                    ol: ({ children }) => <ol className="my-2 ml-4 list-decimal space-y-1">{children}</ol>,
                    li: ({ children }) => <li className="my-1">{children}</li>,
                    h1: ({ children }) => <h1 className="text-lg font-bold my-3">{children}</h1>,
                    h2: ({ children }) => <h2 className="text-base font-bold my-2">{children}</h2>,
                    h3: ({ children }) => <h3 className="text-sm font-semibold my-2">{children}</h3>,
                    strong: ({ children }) => <strong className="font-semibold text-slate-900">{children}</strong>,
                    code: ({ children }) => (
                      <code className="px-1 py-0.5 rounded bg-slate-100 text-slate-700 text-xs font-mono">
                        {children}
                      </code>
                    ),
                    pre: ({ children }) => {
                      const codeChild = React.Children.toArray(children).find(React.isValidElement);
                      const codeChildren = codeChild?.props?.children ?? children;

                      return (
                        <pre className="bg-slate-900 text-slate-100 rounded-lg p-3 my-2 text-xs font-mono overflow-x-auto">
                          <code className="font-mono">{codeChildren}</code>
                        </pre>
                      );
                    },
                    table: ({ children }) => (
                      <div className="my-3 overflow-x-auto rounded-lg border border-slate-200">
                        <table className="min-w-full text-xs">{children}</table>
                      </div>
                    ),
                    thead: ({ children }) => <thead className="bg-slate-50 border-b border-slate-200">{children}</thead>,
                    th: ({ children }) => <th className="px-3 py-2 text-left font-semibold text-slate-700">{children}</th>,
                    td: ({ children }) => <td className="px-3 py-2 border-t border-slate-100">{children}</td>,
                    tr: ({ children }) => <tr className="even:bg-slate-50/50">{children}</tr>,
                  }}
                >
                  {message.content}
                </ReactMarkdown>
              )}
            </div>
          )}

          {messageAttachments.length > 0 && (
            <div className={`mt-2 flex max-w-full flex-wrap gap-1.5 ${isUser ? "justify-end" : "justify-start"}`}>
              {messageAttachments.map((attachment, index) => {
                const attachmentSize = formatAttachmentSize(attachment?.size);
                return (
                  <div
                    key={`${attachment?.filename || "attachment"}-${index}`}
                    className={`inline-flex max-w-full items-center gap-2 rounded-full border px-3 py-1.5 text-xs ${
                      isUser
                        ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                        : "border-slate-200 bg-slate-50 text-slate-600"
                    }`}
                  >
                    <FileText className="h-3.5 w-3.5 flex-shrink-0" />
                    <span className="truncate font-medium">{attachment.filename}</span>
                    {attachmentSize && (
                      <span className={isUser ? "text-emerald-600" : "text-slate-400"}>
                        {attachmentSize}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          
          {message.tool_calls && message.tool_calls.length > 0 && (
            <div className="mt-2 space-y-1">
              {message.tool_calls.map((toolCall, idx) => (
                <div key={idx} className="text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-3 h-3 text-emerald-600" />
                    <span className="font-medium text-slate-700">
                      {toolCall.name?.split('.').pop() || t("carloAgent.action")}
                    </span>
                    {toolCall.status === 'completed' && (
                      <Badge className="ml-auto bg-green-100 text-green-700 border-green-200 text-xs">
                        {t("carloAgent.completed")}
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const activeJobId = latestActiveJobMessage?.metadata?.job_id || null;
  const activeJobMessages = activeJobId
    ? messages.filter((message) => message?.metadata?.job_id === activeJobId)
    : [];
  const activeJobDocumentKind = activeJobMessages.find((message) => message?.metadata?.document_kind)?.metadata?.document_kind;
  const latestActiveJobText = [...activeJobMessages].reverse().find((message) => message?.content)?.content;
  const activeJobStats = activeJobMessages.reduce((stats, message) => {
    const kind = message?.metadata?.kind;
    if (kind === "recipe_created") stats.recipes += 1;
    if (kind === "recipe_updated") stats.recipes += 1;
    if (kind === "inventory_created") stats.inventoryCreated += 1;
    if (kind === "inventory_updated") stats.inventoryUpdated += 1;
    if (kind === "warning") stats.warnings += 1;
    if (kind === "error") stats.errors += 1;
    return stats;
  }, {
    recipes: 0,
    inventoryCreated: 0,
    inventoryUpdated: 0,
    warnings: 0,
    errors: 0,
  });

  const streamBadgeClass = streamStatus === "error"
    ? "border-red-200 bg-red-100 text-red-700"
    : streamStatus === "live"
      ? "border-emerald-200 bg-emerald-100 text-emerald-700"
      : "border-amber-200 bg-amber-100 text-amber-700";
  const streamBadgeLabel = streamStatus === "error"
    ? t("carloAgent.streamError", "Reconnect needed")
    : streamStatus === "live"
      ? t("carloAgent.streamLive", "Live")
      : t("carloAgent.streamConnecting", "Connecting");

  const memoryGraphLayout = useMemo(() => {
    return buildMemoryGraphLayout({
      nodes: memoryGraph.nodes || [],
      links: memoryGraph.links || [],
      positions: memoryNodePositions,
    });
  }, [memoryGraph, memoryNodePositions]);

  const selectedMemoryNode = useMemo(
    () => (memoryGraph.nodes || []).find((node) => node.slug === selectedMemorySlug) || null,
    [memoryGraph.nodes, selectedMemorySlug],
  );

  const getMemoryNodeLabel = (node) => {
    const label = node.title || node.slug;
    return label.length > 24 ? `${label.slice(0, 21)}...` : label;
  };

  const getMemoryGraphPoint = (event) => {
    const svg = memoryGraphSvgRef.current;
    if (!svg) return null;
    const point = svg.createSVGPoint();
    point.x = event.clientX;
    point.y = event.clientY;
    const transformed = point.matrixTransform(svg.getScreenCTM().inverse());
    return {
      x: Math.max(28, Math.min(memoryGraphLayout.width - 28, transformed.x)),
      y: Math.max(28, Math.min(memoryGraphLayout.height - 40, transformed.y)),
    };
  };

  const handleMemoryNodePointerDown = (event, node) => {
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    memoryDragRef.current = { slug: node.slug, pointerId: event.pointerId };
    setDraggingMemorySlug(node.slug);
    selectMemoryNode(node.slug);
  };

  const handleMemoryGraphPointerMove = (event) => {
    const drag = memoryDragRef.current;
    if (!drag.slug || drag.pointerId !== event.pointerId) return;
    const point = getMemoryGraphPoint(event);
    if (!point) return;
    setMemoryNodePositions((current) => ({
      ...current,
      [drag.slug]: point,
    }));
  };

  const stopMemoryNodeDrag = () => {
    memoryDragRef.current = { slug: null, pointerId: null };
    setDraggingMemorySlug(null);
  };

  const renderMemoryGraphView = () => (
    <div className="flex-1 overflow-y-auto bg-slate-50 p-6">
      <div className="mx-auto max-w-6xl space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-slate-900">
              {t("carloAgent.memoryGraph", "Memory graph")}
            </h1>
            <p className="text-sm text-slate-500">
              {t("carloAgent.memoryGraphSubtitle", "Private wiki pages for this user and restaurant.")}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              onClick={() => setShowMemoryForm((current) => !current)}
              className="bg-emerald-700 hover:bg-emerald-800"
            >
              {showMemoryForm ? <X className="mr-2 h-4 w-4" /> : <Plus className="mr-2 h-4 w-4" />}
              {showMemoryForm
                ? t("carloAgent.cancelMemory", "Cancel")
                : t("carloAgent.addMemory", "Add memory")}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleRunMemoryIndex}
              disabled={memoryIndexing}
              className="border-slate-300"
            >
              {memoryIndexing ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Network className="mr-2 h-4 w-4" />
              )}
              {t("carloAgent.indexMemory", "Index")}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => loadMemoryGraph()}
              disabled={memoryGraphLoading}
              className="border-slate-300"
            >
              {memoryGraphLoading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              {t("carloAgent.refresh", "Refresh")}
            </Button>
          </div>
        </div>

        {memoryIndexError ? (
          <p className="text-sm text-red-600">{memoryIndexError}</p>
        ) : null}

        {showMemoryForm && (
          <form
            onSubmit={handleSaveMemory}
            className="rounded-2xl border border-slate-200 bg-white p-4"
          >
            <div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-500">
                  {t("carloAgent.memoryTitle", "Title")}
                </label>
                <Input
                  value={memoryForm.title}
                  onChange={(event) => updateMemoryForm("title", event.target.value)}
                  placeholder={t("carloAgent.memoryTitlePlaceholder", "e.g. May financial report")}
                  className="border-slate-300"
                />
              </div>
            </div>
            <div className="mt-3">
              <label className="mb-1 block text-xs font-medium text-slate-500">
                {t("carloAgent.memoryText", "Memory")}
              </label>
              <textarea
                value={memoryForm.body_markdown}
                onChange={(event) => updateMemoryForm("body_markdown", event.target.value)}
                placeholder={t("carloAgent.memoryTextPlaceholder", "Write the durable context Carlo should remember. Use [[Other Memory Title]] to connect pages.")}
                rows={5}
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
              />
            </div>
            <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
              {memoryFormError ? (
                <p className="text-sm text-red-600">{memoryFormError}</p>
              ) : (
                <p className="text-xs text-slate-500">
                  {t("carloAgent.memoryManualHint", "Saved only for this user and active restaurant. The index updates automatically.")}
                </p>
              )}
              <Button
                type="submit"
                disabled={memorySaving || !memoryForm.title.trim() || !memoryForm.body_markdown.trim()}
                className="bg-slate-900 hover:bg-slate-800"
              >
                {memorySaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {t("carloAgent.saveMemory", "Save memory")}
              </Button>
            </div>
          </form>
        )}

        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_280px]">
          <section className="rounded-2xl border border-slate-200 bg-white p-4">
            {memoryGraphLoading && memoryGraph.nodes.length === 0 ? (
              <div className="flex h-[440px] items-center justify-center text-sm text-slate-500">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("carloAgent.loadingMemoryGraph", "Loading memory graph...")}
              </div>
            ) : memoryGraph.nodes.length === 0 ? (
              <div className="flex h-[440px] items-center justify-center text-center">
                <div>
                  <Network className="mx-auto h-8 w-8 text-slate-300" />
                  <p className="mt-3 text-sm font-medium text-slate-900">
                    {t("carloAgent.memoryGraphEmpty", "No memory pages yet")}
                  </p>
                  <p className="mt-1 text-sm text-slate-500">
                    {t("carloAgent.memoryGraphEmptyHint", "Ask Carlo to remember durable context, then refresh.")}
                  </p>
                </div>
              </div>
            ) : (
              <svg
                ref={memoryGraphSvgRef}
                viewBox={`0 0 ${memoryGraphLayout.width} ${memoryGraphLayout.height}`}
                className="h-[440px] w-full touch-none select-none"
                role="img"
                aria-label={t("carloAgent.memoryGraph", "Memory graph")}
                onPointerMove={handleMemoryGraphPointerMove}
                onPointerUp={stopMemoryNodeDrag}
                onPointerCancel={stopMemoryNodeDrag}
              >
                {memoryGraphLayout.links.map((link) => (
                  <line
                    key={`${link.source}-${link.target}`}
                    x1={link.sourceNode.x}
                    y1={link.sourceNode.y}
                    x2={link.targetNode.x}
                    y2={link.targetNode.y}
                    stroke="#CBD5E1"
                    strokeWidth="1.5"
                    className={draggingMemorySlug ? "" : "transition-all duration-150 ease-out"}
                  />
                ))}
                {memoryGraphLayout.nodes.map((node) => {
                  const selected = node.slug === selectedMemorySlug;
                  const dragging = node.slug === draggingMemorySlug;
                  return (
                    <g
                      key={node.slug}
                      role="button"
                      tabIndex="0"
                      onPointerDown={(event) => handleMemoryNodePointerDown(event, node)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          selectMemoryNode(node.slug);
                        }
                      }}
                      className={`cursor-grab ${dragging ? "cursor-grabbing" : ""}`}
                    >
                      <circle
                        cx={node.x}
                        cy={node.y}
                        r={dragging ? 19 : selected ? 18 : 12}
                        fill={selected ? "#0F172A" : "#F8FAFC"}
                        stroke={selected ? "#0F172A" : "#94A3B8"}
                        strokeWidth="2"
                        className={dragging ? "drop-shadow-sm" : "transition-all duration-150 ease-out"}
                      />
                      <text
                        x={node.x}
                        y={node.y + 30}
                        textAnchor="middle"
                        className={`pointer-events-none fill-slate-700 text-[12px] font-medium ${dragging ? "" : "transition-all duration-150 ease-out"}`}
                      >
                        {getMemoryNodeLabel(node)}
                      </text>
                    </g>
                  );
                })}
              </svg>
            )}
          </section>

          <aside className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                {t("carloAgent.memoryPage", "Memory page")}
              </p>
              {selectedMemoryNode && editingMemorySlug !== selectedMemoryNode.slug ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={startEditingMemory}
                  disabled={selectedMemoryLoading}
                  className="h-8 border-slate-300 px-2"
                >
                  <Pencil className="mr-1 h-3.5 w-3.5" />
                  {t("carloAgent.editMemory", "Edit")}
                </Button>
              ) : null}
            </div>
            {selectedMemoryNode ? (
              <div className="mt-3 space-y-4">
                {editingMemorySlug === selectedMemoryNode.slug ? (
                  <form onSubmit={handleUpdateMemory} className="space-y-3">
                    <div>
                      <label className="mb-1 block text-xs font-medium text-slate-500">
                        {t("carloAgent.memoryTitle", "Title")}
                      </label>
                      <Input
                        value={memoryEditForm.title}
                        onChange={(event) => updateMemoryEditForm("title", event.target.value)}
                        className="border-slate-300"
                      />
                      <p className="mt-1 break-all text-xs text-slate-400">
                        [[{selectedMemoryNode.slug}]]
                      </p>
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-slate-500">
                        {t("carloAgent.memoryText", "Memory")}
                      </label>
                      <textarea
                        value={memoryEditForm.body_markdown}
                        onChange={(event) => updateMemoryEditForm("body_markdown", event.target.value)}
                        rows={10}
                        className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                      />
                    </div>
                    {memoryEditError ? (
                      <p className="text-sm text-red-600">{memoryEditError}</p>
                    ) : null}
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={cancelEditingMemory}
                        disabled={memoryEditSaving}
                        className="border-slate-300"
                      >
                        {t("carloAgent.cancelMemory", "Cancel")}
                      </Button>
                      <Button
                        type="submit"
                        disabled={memoryEditSaving || !memoryEditForm.title.trim() || !memoryEditForm.body_markdown.trim()}
                        className="bg-slate-900 hover:bg-slate-800"
                      >
                        {memoryEditSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                        {t("carloAgent.saveMemory", "Save memory")}
                      </Button>
                    </div>
                  </form>
                ) : (
                  <>
                    <div>
                      <h2 className="text-base font-semibold text-slate-900">
                        {selectedMemoryNode.title || selectedMemoryNode.slug}
                      </h2>
                      <p className="mt-1 break-all text-xs text-slate-500">
                        [[{selectedMemoryNode.slug}]]
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-xs text-slate-400">{t("carloAgent.linksIn", "Links in")}</p>
                        <p className="font-semibold text-slate-900">{selectedMemoryNode.incoming_count || 0}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400">{t("carloAgent.linksOut", "Links out")}</p>
                        <p className="font-semibold text-slate-900">{selectedMemoryNode.outgoing_count || 0}</p>
                      </div>
                    </div>
                    <div>
                      <p className="mb-2 text-xs font-medium text-slate-400">
                        {t("carloAgent.memoryContent", "Content")}
                      </p>
                      <div className="max-h-72 overflow-y-auto rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                        {selectedMemoryLoading ? (
                          <div className="flex items-center text-slate-500">
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            {t("carloAgent.loadingMemoryPage", "Loading memory...")}
                          </div>
                        ) : selectedMemoryError ? (
                          <p className="text-red-600">{selectedMemoryError}</p>
                        ) : selectedMemoryPage?.body_markdown ? (
                          <div className="prose prose-sm max-w-none prose-slate">
                            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSanitize]}>
                              {selectedMemoryPage.body_markdown}
                            </ReactMarkdown>
                          </div>
                        ) : (
                          <p className="text-slate-500">
                            {t("carloAgent.memoryContentEmpty", "No content saved for this page.")}
                          </p>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <p className="mt-3 text-sm text-slate-500">
                {t("carloAgent.selectMemoryPage", "Select a node to inspect it.")}
              </p>
            )}
          </aside>
        </div>
      </div>
    </div>
  );

  const emptyTitle = onboardingMode
    ? t("carloAgent.onboardingHello", "Let's set up Carlo")
    : t("carloAgent.hello");
  const emptyDescription = onboardingMode
    ? t(
        "carloAgent.onboardingDescription",
        "Tell Carlo about your restaurants, sales setup, menu, recipes, suppliers, and shifts. Attach files or photos whenever you have them.",
      )
    : t("carloAgent.description");
  const inputPlaceholder = onboardingMode
    ? t("carloAgent.onboardingPlaceholder", "Tell Carlo what is ready, or attach a file...")
    : t("carloAgent.placeholder");
  const attachButtonLabel = onboardingMode
    ? t("carloAgent.attachSetupFile", "Attach setup file")
    : t("carloAgent.attachFile", "Attach file");

  const chatContent = (
    <div className={`${embedded ? "h-full" : onboardingMode ? "h-screen bg-slate-50" : "h-full -m-4 sm:-m-5 md:-m-6"} flex overflow-x-hidden`}>
      {!onboardingMode && (
        <div className={`${mobileShowChat ? "hidden md:flex" : "flex"} w-full md:w-80 flex-col border-r border-slate-200 bg-white`}>
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-xl flex items-center justify-center shadow-lg">
                <Bot className="w-7 h-7 text-white" />
              </div>
              <div>
                <h2 className="font-bold text-xl text-slate-900">{t("carloAgent.title")}</h2>
                <p className="text-xs text-slate-500 font-medium">{t("carloAgent.subtitle")}</p>
              </div>
            </div>
            <Button
              onClick={createNewConversation}
              className="w-full bg-emerald-700 hover:bg-emerald-800 shadow-md"
            >
              <TrendingUp className="w-4 h-4 mr-2" />
              {t("carloAgent.newAnalysis")}
            </Button>
            <div className="mt-3 grid grid-cols-2 rounded-xl bg-slate-100 p-1">
              <button
                type="button"
                onClick={() => { setActiveView("chat"); setMobileShowChat(true); }}
                className={`flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  activeView === "chat"
                    ? "bg-white text-slate-900 shadow-sm"
                    : "text-slate-500 hover:text-slate-900"
                }`}
              >
                <MessageSquare className="h-4 w-4" />
                {t("carloAgent.chat", "Chat")}
              </button>
              <button
                type="button"
                onClick={() => { setActiveView("memory"); setMobileShowChat(true); }}
                className={`flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  activeView === "memory"
                    ? "bg-white text-slate-900 shadow-sm"
                    : "text-slate-500 hover:text-slate-900"
                }`}
              >
                <Network className="h-4 w-4" />
                {t("carloAgent.memory", "Memory")}
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {conversations.length > 0 && (
              <>
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 px-2">
                  {t("carloAgent.recentConversations")}
                </h3>
                <div className="space-y-2">
                  {conversations.slice(0, 5).map((convo) => (
                    <button
                      key={convo.id}
                      onClick={async () => {
                        setActiveView("chat");
                        setMobileShowChat(true);
                        setCurrentConversationId(convo.id);
                        try {
                          const detail = await base44.agents.getConversation(convo.id);
                          setMessages(detail.messages || []);
                        } catch (err) {
                          console.error("Error loading conversation:", err);
                          setMessages([]);
                        }
                      }}
                      className={`w-full text-left p-3 rounded-xl transition-colors text-sm border ${
                        currentConversationId === convo.id
                          ? "bg-emerald-50 border-emerald-200"
                          : "bg-white border-slate-200 hover:bg-slate-50"
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Calendar className="w-3 h-3 text-slate-400" />
                        <span className="text-xs text-slate-500">
                          {format(new Date(convo.created_date), 'dd.MM.yyyy HH:mm')}
                        </span>
                      </div>
                      <p className="font-medium text-slate-900 truncate">
                        {convo.metadata?.name || 'Carlo Session'}
                      </p>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <div
        className={`${(mobileShowChat || onboardingMode) ? "flex" : "hidden md:flex"} flex-1 min-w-0 flex-col relative`}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {!onboardingMode && (
          <div className="md:hidden flex items-center border-b border-slate-200 bg-white px-4 py-2.5">
            <button
              type="button"
              onClick={() => setMobileShowChat(false)}
              className="flex items-center gap-1 text-sm font-medium text-slate-600 hover:text-slate-900"
            >
              <ChevronLeft className="h-4 w-4" />
              {t("carloAgent.recentConversations", "Conversations")}
            </button>
          </div>
        )}
        {isDraggingFile && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-emerald-950/10 p-6 pointer-events-none">
            <div className="w-full max-w-md rounded-3xl border-2 border-dashed border-emerald-300 bg-white/95 px-6 py-8 text-center shadow-2xl">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
                <FileText className="h-7 w-7" />
              </div>
              <p className="mt-4 text-base font-semibold text-slate-900">
                {t("carloAgent.dropFileHere", "Drop file to attach")}
              </p>
              <p className="mt-1 text-sm text-slate-500">
                {t("carloAgent.dropFileHint", "Files up to 15 MB")}
              </p>
            </div>
          </div>
        )}
        {onboardingMode && (
          <div className="border-b border-slate-200 bg-white px-6 py-4">
            <div className="mx-auto flex max-w-4xl flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-xs font-medium text-emerald-700">
                  {t("carloAgent.onboardingBadge", "Carlo onboarding")}
                </p>
                <h1 className="text-xl font-semibold text-slate-950">
                  {t("carloAgent.onboardingTitle", "Set up your restaurant with Carlo")}
                </h1>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={handleCompleteOnboarding}
                disabled={onboardingCompleting}
                className="border-slate-300"
              >
                {onboardingCompleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {t("carloAgent.openDashboard", "Open dashboard")}
              </Button>
            </div>
            {onboardingError ? (
              <p className="mx-auto mt-2 max-w-4xl text-sm text-red-600">{onboardingError}</p>
            ) : null}
          </div>
        )}
        {activeView === "memory" && !onboardingMode ? (
          renderMemoryGraphView()
        ) : (
          <>
            <div className="flex-1 overflow-y-auto p-6">
              <div className="max-w-4xl mx-auto space-y-6">
                {activeJobId && (
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50/80 px-4 py-4 shadow-sm">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`h-2.5 w-2.5 rounded-full ${streamStatus === "live" ? "bg-emerald-500 animate-pulse" : streamStatus === "error" ? "bg-red-500" : "bg-amber-500"}`} />
                          <p className="text-sm font-semibold text-slate-900">
                            {getOnboardingDocumentLabel(activeJobDocumentKind)}
                          </p>
                          <Badge className={`text-[11px] ${streamBadgeClass}`}>{streamBadgeLabel}</Badge>
                        </div>
                        <p className="mt-1 text-sm text-slate-600">
                          {latestActiveJobText || t("carloAgent.workerPreparing", "Preparing the worker and reading the PDF...")}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {activeJobStats.recipes > 0 && (
                          <Badge className="border-amber-200 bg-amber-100 text-amber-700 text-[11px]">
                            {activeJobStats.recipes} {activeJobDocumentKind === "menu_pdf"
                              ? t("carloAgent.products", "products")
                              : t("carloAgent.recipes", "recipes")}
                          </Badge>
                        )}
                        {activeJobStats.inventoryCreated > 0 && (
                          <Badge className="border-sky-200 bg-sky-100 text-sky-700 text-[11px]">
                            {activeJobStats.inventoryCreated} {t("carloAgent.created", "created")}
                          </Badge>
                        )}
                        {activeJobStats.inventoryUpdated > 0 && (
                          <Badge className="border-sky-200 bg-sky-100 text-sky-700 text-[11px]">
                            {activeJobStats.inventoryUpdated} {t("carloAgent.updated", "updated")}
                          </Badge>
                        )}
                        {activeJobStats.warnings > 0 && (
                          <Badge className="border-amber-200 bg-amber-100 text-amber-700 text-[11px]">
                            {activeJobStats.warnings} {t("carloAgent.warnings", "warnings")}
                          </Badge>
                        )}
                        {activeJobStats.errors > 0 && (
                          <Badge className="border-red-200 bg-red-100 text-red-700 text-[11px]">
                            {activeJobStats.errors} {t("carloAgent.errors", "errors")}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                {messages.length === 0 ? (
                  <div className="text-center py-16">
                    <div className="w-20 h-20 bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-xl">
                      <Bot className="w-10 h-10 text-white" />
                    </div>
                    <h2 className="text-2xl font-bold text-slate-900 mb-3">{emptyTitle}</h2>
                    <p className="text-slate-600 mb-8 max-w-2xl mx-auto">{emptyDescription}</p>
                    {!onboardingMode && !onboardingChatsOnly && (
                      <div className="grid md:grid-cols-2 gap-4 max-w-2xl mx-auto">
                        {quickActions.map((action, idx) => (
                          <Card key={idx} className="border-0 shadow-md hover:shadow-xl transition-all cursor-pointer" onClick={() => setInput(action.prompt)}>
                            <CardContent className="p-4">
                              <p className="font-semibold text-slate-900 text-sm">{action.label}</p>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  messages
                    .filter((message) => !message?.metadata?.hidden_from_chat)
                    .map((message, idx) => (
                    <MessageBubble key={idx} message={message} />
                  ))
                )}
                {isCurrentConversationLoading && (
                  <div className="flex gap-3 justify-start">
                    <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-emerald-600 to-emerald-700 flex items-center justify-center shadow-md flex-shrink-0">
                      <Bot className="w-5 h-5 text-white" />
                    </div>
                    <div className="max-w-[85%] rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
                      <div className="mb-2 flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin text-emerald-600" />
                        <span className="text-sm font-medium text-slate-700">{t("carloAgent.analyzing")}</span>
                      </div>
                      <AgentActivityTimeline activities={agentActivities} pending />
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            </div>

            <div className="border-t border-slate-200 bg-white/80 backdrop-blur-sm p-6">
              <form onSubmit={handleSendMessage} className="max-w-4xl mx-auto">
                {fileError && (
                  <div className="mb-2 text-xs text-red-600">{fileError}</div>
                )}
                {recordingStatus === "idle" ? (
                  <div className="flex items-end gap-2 sm:gap-3">
                    <div className="relative" ref={attachmentMenuRef}>
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        className="hidden"
                        onChange={handleFileChange}
                      />
                      <input
                        ref={cameraInputRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        onChange={handleFileChange}
                      />
                      <Button
                        type="button"
                        onClick={handleAttachClick}
                        disabled={isCurrentConversationLoading}
                        variant="outline"
                        className="h-11 w-11 rounded-2xl border-slate-300 p-0 shadow-sm"
                        aria-label={attachButtonLabel}
                        title={attachButtonLabel}
                      >
                        <Plus className="h-6 w-6" />
                      </Button>
                      {showAttachMenu && (
                        <div className="absolute bottom-full left-0 z-30 mb-2 w-40 rounded-xl border border-slate-200 bg-white p-1 shadow-lg">
                          <button
                            type="button"
                            onClick={openFilePicker}
                            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                          >
                            <FileText className="h-4 w-4 text-slate-500" />
                            {t("carloAgent.attachFiles", "Files")}
                          </button>
                          <button
                            type="button"
                            onClick={openCameraPicker}
                            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                          >
                            <Camera className="h-4 w-4 text-slate-500" />
                            {t("carloAgent.attachCamera", "Camera")}
                          </button>
                        </div>
                      )}
                    </div>
                    <Button
                      type="button"
                      onClick={startRecording}
                      disabled={isCurrentConversationLoading}
                      variant="outline"
                      className="h-11 rounded-2xl border-slate-300 px-3 shadow-sm"
                      aria-label={t("carlo.voiceInput", "Voice input")}
                      title={t("carlo.voiceInput", "Voice input")}
                    >
                      <Mic className="w-5 h-5" />
                    </Button>
                    <div className="flex-1 min-w-0 rounded-2xl border border-slate-300 bg-white shadow-sm transition-colors focus-within:border-emerald-500 focus-within:ring-1 focus-within:ring-emerald-500">
                      {attachedFiles.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 px-3 pt-3">
                          {attachedFiles.map((file, index) => {
                            const AttachmentIcon = file.type?.startsWith("image/") ? ImageIcon : FileText;
                            return (
                              <div
                                key={`${file.name}-${file.size}-${index}`}
                                className="inline-flex max-w-full items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs text-emerald-800"
                              >
                                <AttachmentIcon className="h-3.5 w-3.5 flex-shrink-0" />
                                <span className="max-w-[220px] truncate font-medium">{file.name}</span>
                                <span className="text-emerald-600">{(file.size / 1024 / 1024).toFixed(1)} MB</span>
                                <button
                                  type="button"
                                  onClick={() => clearAttachment(index)}
                                  aria-label={t("carloAgent.removeAttachment")}
                                  className="rounded-full p-0.5 hover:bg-emerald-100 hover:text-emerald-900"
                                >
                                  <X className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      <textarea
                        ref={inputRef}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleComposerKeyDown}
                        placeholder={attachedFiles.length ? t("carloAgent.addMessageWithFile", "Add a message") : inputPlaceholder}
                        rows={1}
                        className="max-h-32 min-h-11 w-full resize-none border-0 bg-transparent px-3 py-3 text-sm leading-5 shadow-none outline-none placeholder:text-slate-400 focus:ring-0 disabled:opacity-50"
                        disabled={isCurrentConversationLoading}
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={isCurrentConversationLoading || (!input.trim() && attachedFiles.length === 0)}
                      className="h-11 rounded-2xl bg-emerald-700 px-6 shadow-md hover:bg-emerald-800"
                    >
                      {isCurrentConversationLoading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Send className="w-5 h-5" />
                      )}
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className={`flex h-10 w-10 items-center justify-center rounded-full ${
                        recordingStatus === "transcribing"
                          ? "bg-amber-100 text-amber-600"
                          : "bg-red-100 text-red-600"
                      }`}>
                        {recordingStatus === "transcribing" ? (
                          <Loader2 className="h-5 w-5 animate-spin" />
                        ) : (
                          <Mic className="h-5 w-5" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-900">
                          {recordingStatus === "transcribing"
                            ? t("carlo.transcribing")
                            : recordingStatus === "paused"
                              ? t("carlo.recordingPaused", "Recording paused")
                              : t("carlo.recording")}
                        </p>
                        <p className="text-xs text-slate-500">
                          {recordingStatus === "transcribing"
                            ? t("carlo.transcribingHint", "Converting voice memo to text...")
                            : formatRecordingTime(recordingSeconds)}
                        </p>
                      </div>
                    </div>

                    {recordingStatus !== "transcribing" && (
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={togglePauseRecording}
                          className="border-slate-300"
                        >
                          {recordingStatus === "paused" ? (
                            <>
                              <Play className="mr-2 h-4 w-4" />
                              {t("carlo.resumeRecording", "Resume")}
                            </>
                          ) : (
                            <>
                              <Pause className="mr-2 h-4 w-4" />
                              {t("carlo.pauseRecording", "Pause")}
                            </>
                          )}
                        </Button>
                        <Button
                          type="button"
                          onClick={stopRecording}
                          className="bg-slate-900 hover:bg-slate-800"
                        >
                          <Square className="mr-2 h-4 w-4" />
                          {t("carlo.stopRecording", "Stop")}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </form>
            </div>
          </>
        )}
      </div>
    </div>
  );

  if (onboardingMode || embedded) {
    return chatContent;
  }

  return <Layout>{chatContent}</Layout>;
}
